import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, UploadCloud, Lock } from "lucide-react";
import ReviewsManager from "@/components/ReviewsManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "pfs-upload-passcode";

const PROPERTIES = [
  ["de-la-vie", "De La Vie Maison"],
  ["heritage", "Heritage"],
  ["hearthstead", "Hearthstead Cottage"],
];

export default function ManagePhotos() {
  const [passcode, setPasscode] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [property, setProperty] = useState("de-la-vie");
  const [files, setFiles] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [availProperty, setAvailProperty] = useState("de-la-vie");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangeBusy, setRangeBusy] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [nudgeKey, setNudgeKey] = useState(0);
  const [requests, setRequests] = useState([]);

  const load = () =>
    axios
      .get(`${API}/photos`)
      .then((r) => setPhotos(r.data))
      .catch(() => {});

  const loadRequests = (code) =>
    axios
      .get(`${API}/maintenance`, { headers: { "X-Upload-Passcode": code } })
      .then((r) => setRequests(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) loadRequests(stored);
  }, []);

  const savePasscode = (v) => {
    setPasscode(v);
    localStorage.setItem(STORAGE_KEY, v);
    if (v) loadRequests(v);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!passcode || files.length === 0) {
      toast.error("Enter the passcode and choose at least one photo.");
      return;
    }
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const body = new FormData();
      body.append("property", property);
      body.append("file", f);
      try {
        await axios.post(`${API}/photos`, body, { headers: { "X-Upload-Passcode": passcode } });
        ok++;
      } catch (err) {
        toast.error(
          err.response?.status === 401 ? "Wrong passcode." : `Could not upload ${f.name}.`
        );
      }
    }
    setBusy(false);
    if (ok > 0) {
      toast.success(
        `${ok} photo${ok > 1 ? "s" : ""} added to ${PROPERTIES.find((p) => p[0] === property)[1]}.`
      );
      setFiles([]);
      e.target.reset();
      load();
    }
  };

  const setRange = async (action) => {
    if (!passcode || !rangeStart || !rangeEnd) {
      toast.error("Enter the passcode and choose a date range.");
      return;
    }
    setRangeBusy(true);
    try {
      const body = { property: availProperty, start: rangeStart, end: rangeEnd, action };
      if (action === "block" && guestEmail.trim()) {
        body.guest_name = guestName.trim();
        body.guest_email = guestEmail.trim();
        body.site_origin = window.location.origin;
      }
      const r = await axios.post(`${API}/availability/range`, body, {
        headers: { "X-Upload-Passcode": passcode },
      });
      toast.success(
        `${r.data.affected} day${r.data.affected > 1 ? "s" : ""} ${action === "block" ? "blocked" : "marked available"} for ${PROPERTIES.find((p) => p[0] === availProperty)[1]}.`
      );
      if (r.data.stay_id) {
        toast.success(`Review invite scheduled for ${guestName.trim() || "the guest"} the day after checkout.`);
        setGuestName("");
        setGuestEmail("");
        setNudgeKey((k) => k + 1);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        err.response?.status === 401
          ? "Wrong passcode."
          : typeof detail === "string"
            ? detail
            : "Could not update availability. Check the guest email address."
      );
    } finally {
      setRangeBusy(false);
    }
  };

  const removeRequest = async (id) => {
    try {
      await axios.delete(`${API}/maintenance/${id}`, { headers: { "X-Upload-Passcode": passcode } });
      setRequests((r) => r.filter((x) => x.id !== id));
      toast.success("Request cleared.");
    } catch {
      toast.error("Could not clear — check your passcode.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/photos/${id}`, { headers: { "X-Upload-Passcode": passcode } });
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success("Photo removed.");
    } catch {
      toast.error("Could not remove — check your passcode.");
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#f7f2e7]/30 focus:border-[#f7f2e7] outline-none py-2.5 text-sm text-[#f7f2e7] placeholder:text-[#f7f2e7]/35 transition-colors duration-300";
  const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#f7f2e7]/60 mb-1";

  return (
    <div data-testid="manage-photos-page" className="min-h-screen bg-[#16301f] text-[#f7f2e7]">
      <div className="max-w-2xl mx-auto px-5 py-12 sm:py-16">
        <div className="text-center mb-10">
          <img src="/images/logo.png" alt="Parker Furnished Stays seal" className="w-20 h-20 mx-auto rounded-full" />
          <h1 className="font-display text-3xl sm:text-4xl mt-6">Photo Manager</h1>
          <p className="font-script text-2xl text-[#c9a227] mt-2">Add photos straight from your phone.</p>
        </div>

        <form data-testid="photo-upload-form" onSubmit={upload} className="border border-[#f7f2e7]/15 bg-[#1a3324] p-6 sm:p-8 space-y-7">
          <div>
            <label htmlFor="mp-passcode" className={labelCls}>
              Passcode
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-[#f7f2e7]/40 absolute left-0 top-3.5" strokeWidth={1.5} />
              <input
                id="mp-passcode"
                data-testid="passcode-input"
                type="password"
                value={passcode}
                onChange={(e) => savePasscode(e.target.value)}
                placeholder="Your private passcode"
                autoComplete="current-password"
                className={`${inputCls} pl-7`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="mp-property" className={labelCls}>
              Residence
            </label>
            <select
              id="mp-property"
              data-testid="property-select"
              value={property}
              onChange={(e) => setProperty(e.target.value)}
              className={`${inputCls} cursor-pointer bg-[#1a3324]`}
            >
              {PROPERTIES.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="mp-files" className={labelCls}>
              Photos
            </label>
            <input
              id="mp-files"
              data-testid="photo-file-input"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="block w-full text-sm text-[#f7f2e7]/70 file:mr-4 file:border file:border-[#f7f2e7]/30 file:bg-transparent file:text-[#f7f2e7] file:text-xs file:uppercase file:tracking-[0.2em] file:px-4 file:py-2.5 hover:file:bg-[#f7f2e7]/10 file:transition-colors file:cursor-pointer"
            />
            {files.length > 0 && (
              <p data-testid="file-count" className="text-xs text-[#f7f2e7]/60 mt-2">
                {files.length} photo{files.length > 1 ? "s" : ""} selected
              </p>
            )}
          </div>
          <button
            data-testid="upload-button"
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#f7f2e7] text-[#16301f] text-xs uppercase tracking-[0.25em] px-6 py-4 hover:bg-[#c9a227] disabled:opacity-60 transition-colors duration-300"
          >
            <UploadCloud className="w-4 h-4" strokeWidth={1.5} />
            {busy ? "Uploading..." : "Add to Gallery"}
          </button>
        </form>

        <div data-testid="availability-manager" className="border border-[#f7f2e7]/15 bg-[#1a3324] p-6 sm:p-8 mt-8">
          <h2 className="font-display text-2xl">Availability</h2>
          <p className="text-xs text-[#f7f2e7]/60 font-light mt-2 mb-7">
            Block the dates a residence is booked — guests see them crossed out on the website.
          </p>
          <div className="space-y-6">
            <div>
              <label htmlFor="avail-property" className={labelCls}>Residence</label>
              <select
                id="avail-property"
                data-testid="avail-property-select"
                value={availProperty}
                onChange={(e) => setAvailProperty(e.target.value)}
                className={`${inputCls} cursor-pointer bg-[#1a3324]`}
              >
                {PROPERTIES.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="avail-start" className={labelCls}>From</label>
                <input
                  id="avail-start"
                  data-testid="avail-start-input"
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
              <div>
                <label htmlFor="avail-end" className={labelCls}>To</label>
                <input
                  id="avail-end"
                  data-testid="avail-end-input"
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>
            </div>
            <div className="border-t border-[#f7f2e7]/10 pt-6">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#c9a227] mb-1">Optional · automatic review invite</p>
              <p className="text-xs text-[#f7f2e7]/60 font-light mb-5">
                Add the guest and they'll be emailed a review invite the morning after checkout.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="avail-guest-name" className={labelCls}>Guest name</label>
                  <input
                    id="avail-guest-name"
                    data-testid="avail-guest-name-input"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    maxLength={80}
                    placeholder="e.g. Amanda"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="avail-guest-email" className={labelCls}>Guest email</label>
                  <input
                    id="avail-guest-email"
                    data-testid="avail-guest-email-input"
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="guest@email.com"
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button
                data-testid="block-dates-btn"
                onClick={() => setRange("block")}
                disabled={rangeBusy}
                className="bg-[#f7f2e7] text-[#16301f] text-xs uppercase tracking-[0.2em] px-4 py-3.5 hover:bg-[#c9a227] disabled:opacity-60 transition-colors duration-300"
              >
                Block dates
              </button>
              <button
                data-testid="unblock-dates-btn"
                onClick={() => setRange("unblock")}
                disabled={rangeBusy}
                className="border border-[#f7f2e7]/30 text-[#f7f2e7] text-xs uppercase tracking-[0.2em] px-4 py-3.5 hover:border-[#f7f2e7] disabled:opacity-60 transition-colors duration-300"
              >
                Mark available
              </button>
            </div>
          </div>
        </div>

        {requests.length > 0 && (
          <div data-testid="maintenance-list" className="border border-[#f7f2e7]/15 bg-[#1a3324] p-6 sm:p-8 mt-8">
            <h2 className="font-display text-2xl">Maintenance Requests</h2>
            <div className="mt-6 space-y-5">
              {requests.map((r) => (
                <div key={r.id} data-testid={`maintenance-item-${r.id}`} className="border-b border-[#f7f2e7]/10 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm">
                        <span
                          className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 mr-2 ${
                            r.urgency === "Emergency"
                              ? "bg-[#8c2f2f] text-[#f7f2e7]"
                              : "bg-[#f7f2e7]/15 text-[#f7f2e7]/80"
                          }`}
                        >
                          {r.urgency}
                        </span>
                        {PROPERTIES.find((p) => p[0] === r.property)?.[1] || r.property} · {r.category}
                      </p>
                      <p className="text-xs text-[#f7f2e7]/70 font-light mt-2">{r.description}</p>
                      <p className="text-[10px] text-[#f7f2e7]/45 mt-1.5">
                        {r.name}
                        {r.contact ? ` · ${r.contact}` : ""} · {new Date(r.created_at).toLocaleDateString()}
                      </p>
                      {r.photo && (
                        <a
                          href={r.photo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] uppercase tracking-[0.15em] text-[#c9a227] hover:text-[#f7f2e7] mt-1.5 inline-block transition-colors duration-300"
                        >
                          View photo
                        </a>
                      )}
                    </div>
                    <button
                      data-testid={`resolve-request-${r.id}`}
                      onClick={() => removeRequest(r.id)}
                      aria-label="Clear request"
                      className="text-[#f7f2e7]/50 hover:text-[#c9a227] shrink-0 transition-colors duration-300"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {passcode && <ReviewsManager passcode={passcode} nudgeKey={nudgeKey} />}

        {photos.length > 0 && (
          <div className="mt-12">
            <p className="text-xs uppercase tracking-[0.25em] text-[#f7f2e7]/60 mb-5">
              Uploaded photos ({photos.length})
            </p>
            <div data-testid="uploaded-photo-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {photos.map((p) => (
                <div key={p.id} className="group relative border border-[#f7f2e7]/15 p-1">
                  <img src={p.src} alt={p.alt} className="w-full h-32 object-cover" loading="lazy" />
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#f7f2e7]/50 mt-1.5 px-0.5">
                    {PROPERTIES.find((x) => x[0] === p.property)?.[1] || p.property}
                  </p>
                  <button
                    data-testid={`delete-photo-${p.id}`}
                    onClick={() => remove(p.id)}
                    aria-label="Remove photo"
                    className="absolute top-2.5 right-2.5 bg-[#16301f]/85 text-[#f7f2e7]/80 hover:text-[#c9a227] p-1.5 transition-colors duration-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-center mt-12">
          <a
            data-testid="back-to-site-link"
            href="/"
            className="text-xs uppercase tracking-[0.22em] text-[#f7f2e7]/60 hover:text-[#c9a227] transition-colors duration-300"
          >
            ← Back to website
          </a>
        </p>
      </div>
    </div>
  );
}
